import { useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { theme } from '@/constants/theme';
import { Mail, Send } from 'lucide-react-native';
import { useAccessibility } from '@/contexts/AccessibilityContext';

interface NoCoursesEmailInputProps {
  onEmailSubmit: (email: string) => Promise<void>;
}

export function NoCoursesEmailInput({ onEmailSubmit }: NoCoursesEmailInputProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const { settings } = useAccessibility();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError('אנא הזן כתובת מייל');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError('כתובת מייל לא תקינה');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onEmailSubmit(trimmedEmail);
      setSubmitted(true);
    } catch (err) {
      console.error('[NoCoursesEmailInput] Error submitting email:', err);
      setError('שגיאה בשמירת המייל. אנא נסה שוב.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <View style={styles.container}>
        <View style={styles.successContainer}>
          <Text style={styles.successIcon}>✓</Text>
          <Text style={styles.successText}>קיבלנו, נעביר אל כתובת המייל שלך את הקורס בהקדם האפשרי .</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.messageContainer}>
        <Mail color={theme.colors.textLight} size={20} />
        <Text style={styles.messageText}>
          לצערנו לא מצאנו קורס שתואם לדרישות שלכם.{'\n'}
          השאירו לנו כאן כתובת מייל אליה נשלח בהקדם האפשרי עדכון לגבי הקורס שחיפשתם
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <Pressable
          style={({ pressed, focused }) => [
            styles.submitButton,
            pressed && styles.submitButtonPressed,
            (!email.trim() || isSubmitting) && styles.submitButtonDisabled,
            settings.keyboardNavigation && focused && styles.submitButtonFocused,
          ]}
          onPress={handleSubmit}
          disabled={!email.trim() || isSubmitting}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="שלח מייל"
          accessibilityState={{ disabled: !email.trim() || isSubmitting }}
        >
          {isSubmitting ? (
            <ActivityIndicator color="#FFFFFF" size="small" />
          ) : (
            <Send color="#FFFFFF" size={18} />
          )}
        </Pressable>

        <TextInput
          style={styles.input}
          value={email}
          onChangeText={(text) => {
            setEmail(text);
            setError(null);
          }}
          placeholder="כתובת מייל"
          placeholderTextColor={theme.colors.textLight}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          editable={!isSubmitting}
          accessible={true}
          accessibilityLabel="כתובת מייל"
          accessibilityHint="הזן את כתובת המייל שלך"
          {...(Platform.OS === 'web' && {
            onSubmitEditing: handleSubmit,
          })}
        />
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginVertical: theme.spacing.sm,
  },
  messageContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.secondary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    marginBottom: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border + '40',
  },
  messageText: {
    flex: 1,
    fontSize: 15,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    textAlign: 'right',
    lineHeight: 22,
  },
  inputContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  input: {
    flex: 1,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.lg,
    paddingVertical: theme.spacing.sm + 2,
    paddingHorizontal: theme.spacing.md,
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    textAlign: 'right',
    borderWidth: 1,
    borderColor: theme.colors.border,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outlineColor: 'transparent',
      outlineOffset: 0,
    }),
  } as any,
  submitButton: {
    backgroundColor: theme.colors.primary,
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonPressed: {
    backgroundColor: theme.colors.primaryDark,
    transform: [{ scale: 0.95 }],
  },
  submitButtonDisabled: {
    backgroundColor: theme.colors.secondary,
    opacity: 0.5,
  },
  submitButtonFocused: {
    borderWidth: 3,
    borderColor: '#FFFFFF',
    boxShadow: `0px 0px 10px ${theme.colors.primary}`,
    elevation: 10,
  },
  errorContainer: {
    marginTop: theme.spacing.xs,
    paddingHorizontal: theme.spacing.sm,
  },
  errorText: {
    fontSize: 13,
    fontFamily: theme.fonts.regular,
    color: theme.colors.error,
    textAlign: 'right',
  },
  successContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.success + '15',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.success + '40',
  },
  successIcon: {
    fontSize: 24,
    color: theme.colors.success,
  },
  successText: {
    flex: 1,
    fontSize: 15,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
    textAlign: 'right',
  },
});
